/**
 * Typewriter — progressively reveal text into a host element, then swap
 * to markdown-rendered HTML on completion. Cancellable via the
 * AbortController-like return value.
 */

import { renderMarkdown } from "./markdown";

const CHARS_PER_TICK = 2;
const TICK_MS = 18;

export interface TypewriterHandle {
  cancel: () => void;
  done: Promise<void>;
}

export function startTypewriter(host: HTMLElement, text: string): TypewriterHandle {
  host.classList.add("typewriter");
  const caret = document.createElement("span");
  caret.className = "typewriter__caret";
  host.appendChild(caret);

  let cursor = 0;
  let cancelled = false;
  let resolveDone!: () => void;
  const done = new Promise<void>((res) => (resolveDone = res));

  const buffer = document.createTextNode("");
  host.insertBefore(buffer, caret);

  function tick(): void {
    if (cancelled) return;
    if (cursor >= text.length) {
      // Finalize: swap raw streamed text for markdown-rendered HTML.
      host.classList.remove("typewriter");
      host.innerHTML = renderMarkdown(text);
      resolveDone();
      return;
    }
    cursor = Math.min(text.length, cursor + CHARS_PER_TICK);
    buffer.nodeValue = text.slice(0, cursor);
    setTimeout(tick, TICK_MS);
  }

  setTimeout(tick, TICK_MS);

  return {
    cancel: () => {
      cancelled = true;
      host.classList.remove("typewriter");
      host.innerHTML = renderMarkdown(text);
      resolveDone();
    },
    done,
  };
}
